import { supabase } from '../../lib/supabaseClient';
import type { Tables, TablesUpdate } from '../../types/database.types';

export type Collaborator = Tables<'collaborators'>;
export type TicketCategory = Tables<'ticket_categories'>;
export type CollaboratorWithCategories = Collaborator & {
  categories: TicketCategory[];
};

export type CollaboratorRole = 'admin' | 'lawyer' | 'secretary';

export interface InviteCollaboratorInput {
  email: string;
  full_name: string;
  role: CollaboratorRole;
  phone?: string | null;
  bar_number?: string | null;
  category_ids: string[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export async function getCollaboratorsWithCategories(): Promise<CollaboratorWithCategories[]> {
  const { data, error } = await supabase
    .from('collaborators')
    .select(`
      *,
      collaborator_categories (
        category_id,
        ticket_categories (*)
      )
    `)
    .order('is_active', { ascending: false })
    .order('full_name', { ascending: true });

  if (error) throw error;

  return (data ?? []).map((row) => {
    const categories = (row.collaborator_categories ?? [])
      .map((cc: { ticket_categories: TicketCategory | null }) => cc.ticket_categories)
      .filter((c): c is TicketCategory => c !== null);

    const { collaborator_categories: _omit, ...rest } = row as typeof row & {
      collaborator_categories: unknown;
    };
    void _omit;

    return { ...rest, categories } as CollaboratorWithCategories;
  });
}

export async function getCollaboratorById(id: string): Promise<CollaboratorWithCategories | null> {
  const { data, error } = await supabase
    .from('collaborators')
    .select(`
      *,
      collaborator_categories (
        category_id,
        ticket_categories (*)
      )
    `)
    .eq('id', id)
    .single();

  if (error) return null;
  if (!data) return null;

  const categories = ((data.collaborator_categories as Array<{ ticket_categories: TicketCategory | null }>) ?? [])
    .map((cc) => cc.ticket_categories)
    .filter((c): c is TicketCategory => c !== null);

  const { collaborator_categories: _omit, ...rest } = data as typeof data & {
    collaborator_categories: unknown;
  };
  void _omit;

  return { ...rest, categories } as CollaboratorWithCategories;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function updateCollaborator(
  id: string,
  input: TablesUpdate<'collaborators'>,
): Promise<void> {
  // is_active doit passer exclusivement par set-collaborator-active (ban Auth).
  // On le retire silencieusement du payload PostgREST.
  const { is_active: _ignored, ...safeInput } = input;
  void _ignored;
  const { error } = await supabase
    .from('collaborators')
    .update({ ...safeInput, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function uploadAvatar(collaboratorId: string, file: File): Promise<string> {
  const ext = file.name.split('.').pop() ?? 'jpg';
  const path = `${collaboratorId}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(path, file, { upsert: true, contentType: file.type });

  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('avatars').getPublicUrl(path);
  const publicUrl = `${data.publicUrl}?t=${Date.now()}`;

  // RPC SECURITY DEFINER (self) : l'UPDATE direct est réservé aux admins. La RPC
  // ne modifie que avatar_url sur la ligne du caller.
  const { error } = await supabase.rpc('update_my_avatar', { p_avatar_url: publicUrl });
  if (error) throw error;

  return publicUrl;
}

export async function setCollaboratorCategories(
  collaboratorId: string,
  categoryIds: string[],
): Promise<void> {
  // Delete all existing then insert new ones
  const { error: deleteError } = await supabase
    .from('collaborator_categories')
    .delete()
    .eq('collaborator_id', collaboratorId);

  if (deleteError) throw deleteError;

  if (categoryIds.length === 0) return;

  const rows = categoryIds.map((category_id) => ({
    collaborator_id: collaboratorId,
    category_id,
  }));

  const { error: insertError } = await supabase
    .from('collaborator_categories')
    .insert(rows);

  if (insertError) throw insertError;
}

export async function deactivateCollaborator(id: string): Promise<void> {
  // Passer par l'Edge Function pour révoquer aussi la session Auth (ban permanent).
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');
  const { error } = await supabase.functions.invoke('set-collaborator-active', {
    body: { collaborator_id: id, active: false },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw error;
}

export async function activateCollaborator(id: string): Promise<void> {
  // Passer par l'Edge Function pour lever le ban Auth en même temps.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');
  const { error } = await supabase.functions.invoke('set-collaborator-active', {
    body: { collaborator_id: id, active: true },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw error;
}

export async function updateMyFormAutoAssign(
  _collaboratorId: string,
  accept: boolean,
): Promise<void> {
  // RPC SECURITY DEFINER : un UPDATE direct sur `collaborators` est réservé aux admins
  // (sinon escalade de privilèges via la colonne `role`). La RPC ne touche que
  // accept_form_auto_assign sur la ligne du caller (auth.uid()).
  const { error } = await supabase.rpc('update_my_form_auto_assign', { p_accept: accept });
  if (error) throw error;
}

export async function inviteCollaborator(
  input: InviteCollaboratorInput,
): Promise<{ collaborator_id: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');

  const { data, error } = await supabase.functions.invoke<{ collaborator_id: string }>(
    'invite-collaborator',
    { body: input, headers: { Authorization: `Bearer ${session.access_token}` } },
  );

  if (error) throw error;
  if (!data?.collaborator_id) throw new Error('Réponse invalide du serveur');

  return { collaborator_id: data.collaborator_id };
}

export async function resendCollaboratorInvitation(collaboratorId: string): Promise<void> {
  // Passer le token explicitement — functions.invoke() peut utiliser la publishable key
  // (non-JWT) comme fallback si l'auth n'est pas encore initialisée, ce qui provoque un 401.
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Non authentifié');

  const { error } = await supabase.functions.invoke('resend-collaborator-invitation', {
    body: { collaborator_id: collaboratorId },
    headers: { Authorization: `Bearer ${session.access_token}` },
  });
  if (error) throw error;
}
